import React, { useLayoutEffect, useRef, useState } from 'react';

export interface SegmentedOption<T> {
  value: T;
  label: React.ReactNode;
}

interface SegmentedProps<T> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Tabs switch what's shown (Results); radios pick a setting (Practice) */
  kind?: 'radio' | 'tab';
  /** Keep keyboard focus where it is (the typing test) when an option is clicked */
  keepFocus?: boolean;
}

// A row of options with a highlight that slides to the selected one
export function Segmented<T extends string | number>({ options, value, onChange, ariaLabel, kind = 'radio', keepFocus }: SegmentedProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  // No slide on the first placement, only when the selection changes
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const active = track.querySelector<HTMLElement>('[data-active="true"]');
      if (!active) return;
      const next = { left: active.offsetLeft, width: active.offsetWidth };
      setIndicator(prev => (prev && prev.left === next.left && prev.width === next.width ? prev : next));
    };
    measure();
    // Re-measure when fonts load or labels change width
    const observer = new ResizeObserver(measure);
    track.querySelectorAll('button').forEach(b => observer.observe(b));
    return () => observer.disconnect();
  }, [value, options]);

  useLayoutEffect(() => {
    if (indicator && !animate) {
      const id = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(id);
    }
  }, [indicator, animate]);

  return (
    <div ref={trackRef} className="segmented" role={kind === 'tab' ? 'tablist' : 'radiogroup'} aria-label={ariaLabel}>
      {indicator && (
        <span
          className={`segmented-indicator${animate ? ' animate' : ''}`}
          style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          aria-hidden="true"
        />
      )}
      {options.map(opt => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role={kind}
            {...(kind === 'tab' ? { 'aria-selected': active } : { 'aria-checked': active })}
            data-active={active}
            className={active ? 'active' : ''}
            onMouseDown={keepFocus ? e => e.preventDefault() : undefined}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
